import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useHistory, useLocation } from 'react-router-dom';
import { instance, createOrder, cancelPayment } from '@api';
import { useOrderContext, useAuthContext, useLoadingContext } from '@hooks';
import Header from '@components/Header/Header';
import { getApiUrl } from '@utils';
import OrdersScreenComponent from './OrdersScreenComponent';
import { verifyIdealPayment } from '../../api/payment/verifyiDealPayment';
import { ROUTES } from '@constants';
import SignIn from '../SignIn';
import VerificationCodeScreen from '../VerificationCodeScreen';
import { useTranslation } from 'react-i18next';

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      // cssSrc: 'https://fonts.googleapis.com/css?family=Roboto',
    },
  ],
  // locale: window.__exampleLocale,
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const OrdersScreen = () => {
  const query = useQuery();
  const { order, setOrder } = useOrderContext();
  const { setIsLoading } = useLoadingContext();
  const history = useHistory();
  const { setSession } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [authModal, showAuthModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [email, setEmail] = useState('');
  const [isVerificationScreen, updateIsVerificationScreen] = useState(false);

  const [isShowStripe, setIsShowStripe] = useState(false);
  const [payment, setPayment] = useState({});
  const [stripePromise, setStripePromise] = useState({});
  const { t } = useTranslation('auth');
  const sessionString = localStorage.getItem('session');
  const sessionParsed = JSON.parse(sessionString);

  useEffect(() => {
    const redirectStatus = query.get('redirect_status');

    if (redirectStatus === 'succeeded' && !open) {
      onPaymentSuccess();
    }
    if (redirectStatus === 'failed' && !open) {
      onPaymentFailed();
    }
    // eslint-disable-next-line
  }, [query, open]);

  useEffect(() => {
    const currentSession = JSON.parse(localStorage.getItem('session'));

    if (!currentSession || Object.keys(currentSession).length === 0) {
      setSession({});
      history.push('/');
    } else {
      setOpen(false);
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  const handleClose = async () => {
    setOpen(false);
    showAuthModal(false);
    let products = [];
    order.forEach(item => {
      let product = {};
      for (const [key, value] of Object.entries(item)) {
        if (key === 'id') {
          product['id'] = value;
        }
        if (key === 'qty') {
          product['quantity'] = value;
        }
        if (
          key === 'promoted' &&
          sessionParsed.visit.promoteProductsTestGroup === 'a'
        ) {
          product['promotionSelected'] = true;
        }
      }
      products.push(product);
    });
    if (products.length === 0) {
      return;
    }
    setIsLoading(true);
    const sendData = {
      products,
      instructions: '',
    };
    const res_order = await createOrder(sendData);
    sessionStorage.setItem('orderId', res_order.order.id);
    sessionStorage.setItem('orderPrice', totalPrice);

    const psk = localStorage.getItem('psk');
    const s_p = loadStripe(psk);
    setStripePromise(s_p);
    setIsShowStripe(true);
  };

  const totalPrice = order
    .map(item => item.qty * item.priceIncludingTax)
    .reduce((a, b) => a + b, 0);

  const handleOrder = async () => {
    /*
     * handle order
     */
    let products = [];
    order.forEach(item => {
      let product = {};
      for (const [key, value] of Object.entries(item)) {
        if (key === 'id') {
          product['id'] = value;
        }
        if (key === 'qty') {
          product['quantity'] = value;
        }
        if (
          key === 'promoted' &&
          sessionParsed.visit.promoteProductsTestGroup === 'a'
        ) {
          product['promotionSelected'] = true;
        }
      }
      products.push(product);
    });
    if (products.length === 0) {
      return;
    }
    setIsLoading(true);
    const send_data = {
      products,
      instructions: '',
    };
    try {
      const currentSession = JSON.parse(localStorage.getItem('session'));
      const screen = sessionStorage.getItem('showPay');
      if (!currentSession.authenticated && screen !== 'pay') {
        showAuthModal(true);
      } else {
        const res_order = await createOrder(send_data);
        sessionStorage.setItem('orderId', res_order.order.id);
        sessionStorage.setItem('orderPrice', totalPrice);
        const psk = localStorage.getItem('psk');

        const s_p = loadStripe(psk);
        setStripePromise(s_p);
        setIsShowStripe(true);
      }
    } catch (err) {
      console.error(err);
      if (err.message.includes('401')) {
        setSession({});
        history.push('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const OpenVerificationScreen = () => {
    updateIsVerificationScreen(true);
    setTimeout(() => {
      showAuthModal(false);
    }, 800);
  };

  const onPaymentSuccess = async () => {
    if (query.get('redirect_status') === 'succeeded' && !paymentProcessing) {
      setPaymentProcessing(true);
      await verifyIdealPayment({
        paymentId: query.get('paymentId'),
      });
      setOrder([]);
      setPayment({});
      setIsShowStripe(false);
      // setOpen(true);
      setPaymentProcessing(false);
      history.push(ROUTES.THANK_YOU_SCREEN, {
        from: ROUTES.ORDERS_SCREEN,
      });
    }
  };

  const onPaymentFailed = async () => {
    setPaymentProcessing(true);
    await cancelPayment(query.get('paymentId'));
    setPaymentProcessing(false);
  };

  const CloseVerificationScreen = async () => {
    setOpen(false);
    showAuthModal(false);
    let products = [];
    order.forEach(item => {
      let product = {};
      for (const [key, value] of Object.entries(item)) {
        if (key === 'id') {
          product['id'] = value;
        }
        if (key === 'qty') {
          product['quantity'] = value;
        }
        if (
          key === 'promoted' &&
          sessionParsed.visit.promoteProductsTestGroup === 'a'
        ) {
          product['promotionSelected'] = true;
        }
      }
      products.push(product);
    });
    if (products.length === 0) {
      return;
    }
    setIsLoading(true);
    const sendData = {
      products,
      instructions: '',
    };
    const res_order = await createOrder(sendData);
    sessionStorage.setItem('orderId', res_order.order.id);
    sessionStorage.setItem('orderPrice', totalPrice);
    const psk = localStorage.getItem('psk');

    const s_p = loadStripe(psk);
    setStripePromise(s_p);
    setIsShowStripe(true);

    setIsLoading(false);

    updateIsVerificationScreen(false);
  };

  const onCancelPayment = async () => {
    const req_conf = {
      method: 'delete',
      url: `${getApiUrl()}/payments/${payment.id}`,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      await instance(req_conf);
      setPayment({});
      setStripePromise(undefined);
    } catch (err) {
      console.error(err);
    }

    setIsShowStripe(false);
  };

  return (
    <div>
      <Header />
      <OrdersScreenComponent
        isShowStripe={isShowStripe}
        order={order}
        totalPrice={totalPrice}
        handleOrder={handleOrder}
        open={open}
        handleClose={handleClose}
        stripePromise={stripePromise}
        payment={payment}
        setOrder={setOrder}
        setPayment={setPayment}
        onPaymentSuccess={onPaymentSuccess}
        onCancelPayment={onCancelPayment}
        elementsOptions={ELEMENTS_OPTIONS}
      />
      <SignIn
        open={authModal}
        handleClose={handleClose}
        title={t('signIn.title')}
        subTitle={'Sign in with one click to receive a receipt of your order.'}
        OpenVerificationScreen={OpenVerificationScreen}
        email={email}
        screen={'pay'}
        setEmail={setEmail}
      />
      <VerificationCodeScreen
        isVerificationScreen={isVerificationScreen}
        handleClose={CloseVerificationScreen}
        root={ROUTES.ORDERS_SCREEN}
        email={email}
        setEmail={setEmail}
      />
    </div>
  );
};

export default OrdersScreen;
